import { useState, useEffect } from 'react';
import { WifiOff, Wifi } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const OfflineIndicator = () => {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [showReconnect, setShowReconnect] = useState(false);
  const [queuedActions, setQueuedActions] = useState(0);

  useEffect(() => {
    const goOffline = () => {
      setIsOffline(true);
      setShowReconnect(false);
    };
    const goOnline = () => {
      setIsOffline(false);
      setShowReconnect(true);
      // Notify SW to sync
      if (navigator.serviceWorker?.controller) {
        navigator.serviceWorker.controller.postMessage('ONLINE');
      }
      setTimeout(() => setShowReconnect(false), 3000);
    };

    const onMessage = (event) => {
      if (event.data?.type === 'OFFLINE_QUEUED') {
        setQueuedActions(prev => prev + 1);
      }
      if (event.data?.type === 'SYNC_COMPLETE') {
        setQueuedActions(event.data.remaining || 0);
      }
    };

    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    navigator.serviceWorker?.addEventListener('message', onMessage);

    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
      navigator.serviceWorker?.removeEventListener('message', onMessage);
    };
  }, []);

  return (
    <AnimatePresence>
      {isOffline && (
        <motion.div
          initial={{ y: -40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -40, opacity: 0 }}
          className="fixed top-0 left-0 right-0 z-[100] bg-amber-600 text-white text-center py-2 px-4 text-sm font-medium flex items-center justify-center gap-2"
          data-testid="offline-banner"
        >
          <WifiOff className="w-4 h-4" />
          You're offline — viewing cached data
          {queuedActions > 0 && (
            <span className="ml-2 px-2 py-0.5 bg-white/20 rounded-full text-xs">{queuedActions} queued</span>
          )}
        </motion.div>
      )}
      {showReconnect && !isOffline && (
        <motion.div
          initial={{ y: -40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -40, opacity: 0 }}
          className="fixed top-0 left-0 right-0 z-[100] bg-emerald-600 text-white text-center py-2 px-4 text-sm font-medium flex items-center justify-center gap-2"
          data-testid="online-banner"
        >
          <Wifi className="w-4 h-4" />
          Back online — syncing...
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default OfflineIndicator;
